import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Plus,
  MoreHorizontal,
  Building2,
  Edit,
  Trash2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCompanies } from "@/hooks/useCompanies";
import { Alert, AlertDescription } from "@/components/ui/alert";
import CompanyModal from "@/components/modals/CompanyModal";
import EditCompanyModal from "@/components/modals/EditCompanyModal";

const Companies = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [editCompanyModalOpen, setEditCompanyModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  // Integração com o backend
  const {
    companies,
    isLoading,
    error,
    createCompany,
    updateCompany,
    deleteCompany,
    isCreating,
    isUpdating,
    isDeleting,
  } = useCompanies();

  const filteredCompanies = companies.filter(
    (company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.cnpj?.includes(searchTerm)
  );

  const handleAddCompany = () => {
    setCompanyModalOpen(true);
  };

  const handleEditCompany = (company: any) => {
    setSelectedCompany(company);
    setEditCompanyModalOpen(true);
  };

  const handleDeleteCompany = (company: any) => {
    if (
      window.confirm(
        `Tem certeza que deseja deletar a empresa "${company.name}"?`
      )
    ) {
      deleteCompany(company.id);
    }
  };

  const handleSaveCompany = (data: any) => {
    createCompany(data);
    setCompanyModalOpen(false);
  };

  const handleUpdateCompany = (data: any) => {
    if (selectedCompany) {
      updateCompany({ id: selectedCompany.id, companyData: data });
      setEditCompanyModalOpen(false);
      setSelectedCompany(null);
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Erro ao carregar empresas:{" "}
            {(error as any)?.message || "Erro desconhecido"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Gerenciamento de Empresas
          </h1>
          <p className="text-muted-foreground mt-2">
            Gerencie todas as empresas do sistema
          </p>
        </div>
        <Button
          onClick={handleAddCompany}
          className="flex items-center gap-2"
          disabled={isCreating}
        >
          <Plus className="h-4 w-4" />
          Nova Empresa
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Empresas</CardTitle>
          <CardDescription>
            Lista de todas as empresas cadastradas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar empresas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">Carregando empresas...</span>
            </div>
          ) : (
            <>
              {/* Grid Layout para os Cards das Empresas */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredCompanies.map((company) => (
                  <Card
                    key={company.id}
                    className="hover:shadow-md transition-all duration-200 hover:scale-[1.02] relative group"
                  >
                    <CardContent className="p-4">
                      {/* Header do Card com Ícone e Dropdown */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-primary" />
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              disabled={isUpdating || isDeleting}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleEditCompany(company)}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteCompany(company)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Deletar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Informações da Empresa */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-foreground text-lg truncate">
                            {company.name}
                          </h3>
                          <Badge
                            variant="default"
                            className="ml-2 flex-shrink-0"
                          >
                            Ativa
                          </Badge>
                        </div>

                        <div className="text-sm text-muted-foreground space-y-1">
                          <p className="truncate">
                            <span className="font-medium">CNPJ:</span>{" "}
                            {company.cnpj || "N/A"}
                          </p>
                          <p className="truncate">
                            <span className="font-medium">Telefone:</span>{" "}
                            {company.telephone || "N/A"}
                          </p>
                        </div>
                      </div>

                      {/* Footer do Card */}
                      <div className="mt-4 pt-3 border-t border-border/50">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Empresa ativa</span>
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Online
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Estado Vazio */}
              {filteredCompanies.length === 0 && !isLoading && (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">
                    Nenhuma empresa encontrada
                  </h3>
                  <p className="text-sm mb-4">
                    {searchTerm
                      ? "Tente ajustar os termos de busca"
                      : "Comece criando sua primeira empresa"}
                  </p>
                  {!searchTerm && (
                    <Button onClick={handleAddCompany} className="mt-2">
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Primeira Empresa
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <CompanyModal
        open={companyModalOpen}
        onOpenChange={setCompanyModalOpen}
        onSave={handleSaveCompany}
      />

      <EditCompanyModal
        open={editCompanyModalOpen}
        onOpenChange={setEditCompanyModalOpen}
        company={selectedCompany}
        onSave={handleUpdateCompany}
      />
    </div>
  );
};

export default Companies;
